import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { money, fileUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2, Trash2, Plus } from "lucide-react";

const empty = {
  name: "", sku: "", description: "", images: [], category_id: null,
  quantity: 0, cost: 0, base_price: 0, active: true, featured: false,
  tags: [], customer_prices: [], group_prices: [],
};

export default function ProductEditor() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [form, setForm] = useState(empty);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [newCat, setNewCat] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEdit = !!pid;

  useEffect(() => {
    api.get("/store/categories").then((r) => setCategories(r.data));
    api.get("/store/customers").then((r) => setCustomers(r.data));
    api.get("/store/price-sheets").then((r) => setSheets(r.data));
    if (pid) api.get(`/store/products/${pid}`).then((r) => setForm({ ...empty, ...r.data }));
  }, [pid]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadFile(f));
      set("images", [...form.images, ...paths]);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    const { data } = await api.post("/store/categories", { name: newCat.trim() });
    setCategories((c) => [...c, data]);
    set("category_id", data.id);
    setNewCat("");
  };

  const setCustomerPrice = (customerId, price) => {
    const others = form.customer_prices.filter((c) => c.customer_id !== customerId);
    if (price === "" || price == null) set("customer_prices", others);
    else set("customer_prices", [...others, { customer_id: customerId, price: parseFloat(price) }]);
  };

  const save = async () => {
    if (!form.name || form.base_price === "" ) { toast.error("Name and base price are required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      quantity: parseInt(form.quantity) || 0,
      cost: parseFloat(form.cost) || 0,
      base_price: parseFloat(form.base_price) || 0,
    };
    try {
      if (isEdit) await api.put(`/store/products/${pid}`, payload);
      else await api.post("/store/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      navigate("/dashboard/products");
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/store/products/${pid}`);
    toast.success("Product deleted");
    navigate("/dashboard/products");
  };

  const custName = (id) => customers.find((c) => c.id === id)?.name || "Customer";

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate("/dashboard/products")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-products">
        <ArrowLeft className="h-4 w-4" /> Back to products
      </button>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{isEdit ? "Edit product" : "New product"}</h1>
        {isEdit && <Button variant="ghost" size="icon" onClick={del} className="text-destructive" data-testid="delete-product-btn"><Trash2 className="h-4 w-4" /></Button>}
      </div>

      <Card className="p-6 rounded-xl border-border space-y-5">
        <div className="space-y-2">
          <Label>Product name *</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11" data-testid="product-name-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>SKU</Label>
            <Input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="h-11" data-testid="product-sku-input" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={form.category_id || "none"} onValueChange={(v) => set("category_id", v === "none" ? null : v)}>
              <SelectTrigger className="h-11" data-testid="product-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Uncategorized</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 pt-1">
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category" className="h-9 text-sm" data-testid="new-category-input" />
              <Button type="button" variant="outline" size="sm" onClick={addCategory} className="h-9"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="product-description-input" />
        </div>

        <div className="space-y-2">
          <Label>Images</Label>
          <div className="flex flex-wrap gap-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-border group">
                <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => set("images", form.images.filter((_, idx) => idx !== i))}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="h-20 w-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid="upload-image-btn">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Cost</Label>
            <Input type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} className="h-11" data-testid="product-cost-input" />
          </div>
          <div className="space-y-2">
            <Label>Base price *</Label>
            <Input type="number" step="0.01" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} className="h-11" data-testid="product-price-input" />
          </div>
          <div className="space-y-2">
            <Label>Quantity</Label>
            <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="h-11" data-testid="product-quantity-input" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Tags</Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 bg-secondary rounded-full px-2.5 py-1 text-xs font-medium">
                {t}<button onClick={() => set("tags", form.tags.filter((x) => x !== t))}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="Add a tag and press Enter" className="h-10" data-testid="product-tag-input" />
            <Button type="button" variant="outline" onClick={addTag} className="h-10">Add</Button>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-switch" />
            <Label>Active</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="product-featured-switch" />
            <Label>Featured</Label>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-xl border-border">
        <h3 className="font-semibold mb-1">Customer-specific pricing</h3>
        <p className="text-sm text-muted-foreground mb-4">Override the base price for individual customers. Leave blank to use base price.</p>
        {customers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No customers yet. Add customers to set custom prices.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {customers.map((c) => {
              const cp = form.customer_prices.find((x) => x.customer_id === c.id);
              return (
                <div key={c.id} className="flex items-center justify-between gap-3 py-1.5">
                  <span className="text-sm font-medium truncate">{c.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">{money(form.base_price)} →</span>
                    <Input type="number" step="0.01" defaultValue={cp?.price ?? ""} placeholder="Base"
                      onBlur={(e) => setCustomerPrice(c.id, e.target.value)}
                      className="h-9 w-28" data-testid={`custom-price-${c.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {isEdit && (
        <Card className="p-6 rounded-xl border-border">
          <h3 className="font-semibold mb-1">Price sheets using this product</h3>
          <p className="text-sm text-muted-foreground mb-4">Sheets that set a specific price for this product. Manage prices from the Price Sheets page.</p>
          {sheets.filter((s) => (s.prices || []).some((p) => p.product_id === pid)).length === 0 ? (
            <p className="text-sm text-muted-foreground">No price sheets price this product yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {sheets.filter((s) => (s.prices || []).some((p) => p.product_id === pid)).map((s) => {
                const entry = s.prices.find((p) => p.product_id === pid);
                return (
                  <span key={s.id} className="inline-flex items-center gap-2 bg-secondary rounded-full px-3 py-1.5 text-sm font-medium" data-testid={`product-sheet-${s.id}`}>
                    {s.name} · {money(entry.price)}
                  </span>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/dashboard/products")}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-product-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save changes" : "Create product")}
        </Button>
      </div>
    </div>
  );
}
